import React, { useState, useEffect } from 'react';
import { Trophy, Clock, Star } from 'lucide-react';
import { MatchplayTournament, MatchplayMatch, MatchplayPlayer } from '../../../types/matchplay-v2';
import { MatchplayDatabase } from '../../../lib/matchplay-v2/database';
import { MatchplayScoring } from '../../../lib/matchplay-v2/scoring';

// Custom CSS for arrow shapes
const arrowStyles = `
  .clip-path-arrow-right {
    clip-path: polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%);
  }
  .clip-path-arrow-left {
    clip-path: polygon(15% 0%, 100% 0%, 100% 100%, 15% 100%, 0% 50%);
  }
`;

interface MatchplayLeaderboardViewProps {
  tournamentId: string;
}

export function MatchplayLeaderboardView({ tournamentId }: MatchplayLeaderboardViewProps) {
  const [tournament, setTournament] = useState<MatchplayTournament | null>(null);
  const [matches, setMatches] = useState<MatchplayMatch[]>([]);
  const [players, setPlayers] = useState<MatchplayPlayer[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadTournamentData();
  }, [tournamentId]);

  const loadTournamentData = async () => {
    setIsLoading(true);
    try {
      const [tournamentData, matchesData, playersData] = await Promise.all([
        MatchplayDatabase.getTournament(tournamentId),
        MatchplayDatabase.getMatches(tournamentId),
        MatchplayDatabase.getPlayers(tournamentId)
      ]);

      setTournament(tournamentData);
      setMatches(matchesData);
      setPlayers(playersData);
    } catch (error) {
      console.error('Error loading tournament data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTeamNames = (match: MatchplayMatch, players: MatchplayPlayer[]) => {
    const team1PlayerIds = match.team1Players || [];
    const team2PlayerIds = match.team2Players || [];

    const team1PlayersData = players.filter(p => team1PlayerIds.includes(p.id));
    const team2PlayersData = players.filter(p => team2PlayerIds.includes(p.id));

    if (tournament?.format === 'singles') {
      return {
        team1: team1PlayersData[0]?.playerName || 'Player 1',
        team2: team2PlayersData[0]?.playerName || 'Player 2'
      };
    } else {
      const team1Names = team1PlayersData.map(p => p.playerName).join(' / ');
      const team2Names = team2PlayersData.map(p => p.playerName).join(' / ');
      return {
        team1: team1Names,
        team2: team2Names
      };
    }
  };

  const getMatchStatus = (match: MatchplayMatch) => {
    if (!Array.isArray(match.holes) || match.holes.length === 0) {
      return { text: 'AS', color: 'bg-gray-100 text-gray-800', arrow: null, leadingTeam: null };
    }
    
    const statusCalc = MatchplayScoring.calculateMatchStatus(match);
    const holesPlayed = match.holes.length;
    
    // Calculate the actual lead direction
    const team1Wins = match.holes.filter(h => h.holeWinner === 'team1').length;
    const team2Wins = match.holes.filter(h => h.holeWinner === 'team2').length;
    const actualLead = team1Wins - team2Wins;
    
    if (statusCalc.status === 'completed') {
      // For completed matches, determine winner and show arrow
      if (statusCalc.winner === 'team1') {
        return { 
          text: statusCalc.display || 'Completed', 
          color: 'bg-red-100 text-red-800',
          arrow: '←',
          leadingTeam: 'A'
        };
      } else if (statusCalc.winner === 'team2') {
        return { 
          text: statusCalc.display || 'Completed', 
          color: 'bg-blue-100 text-blue-800',
          arrow: '→',
          leadingTeam: 'B'
        };
      } else {
        // Tie
        return { 
          text: statusCalc.display || 'Completed', 
          color: 'bg-gray-100 text-gray-800',
          arrow: null,
          leadingTeam: null
        };
      }
    } else if (holesPlayed === 0) {
      return { text: 'AS', color: 'bg-gray-100 text-gray-800', arrow: null, leadingTeam: null };
    } else {
      // Use the same display logic as the main component but customize for leaderboard
      let displayText = statusCalc.display || `AS thru ${holesPlayed}`;
      
      // Customize display text for leaderboard view
      if (actualLead > 0) {
        // Team A is leading
        displayText = `${Math.abs(actualLead)}UP thru ${holesPlayed}`;
      } else if (actualLead < 0) {
        // Team B is leading
        displayText = `${Math.abs(actualLead)}UP thru ${holesPlayed}`;
      } else {
        // Tied
        displayText = `AS thru ${holesPlayed}`;
      }
      
      // Determine which team is leading based on actual lead
      if (actualLead > 0) {
        return { 
          text: displayText, 
          color: 'bg-red-100 text-red-800',
          arrow: '←',
          leadingTeam: 'A'
        };
      } else if (actualLead < 0) {
        return { 
          text: displayText, 
          color: 'bg-blue-100 text-blue-800',
          arrow: '→',
          leadingTeam: 'B'
        };
      } else {
        return { 
          text: displayText, 
          color: 'bg-gray-100 text-gray-800',
          arrow: null,
          leadingTeam: null
        };
      }
    }
  };

  // Helper functions for match counting
  const getActiveMatches = () => {
    if (!Array.isArray(matches)) return [];
    return matches.filter(match => {
      if (!Array.isArray(match.holes)) return true;
      const statusCalc = MatchplayScoring.calculateMatchStatus(match);
      return statusCalc.status !== 'completed';
    });
  };

  const getCompletedMatches = () => {
    if (!Array.isArray(matches)) return [];
    return matches.filter(match => {
      if (!Array.isArray(match.holes)) return false;
      const statusCalc = MatchplayScoring.calculateMatchStatus(match);
      return statusCalc.status === 'completed';
    });
  };

  const activeMatches = getActiveMatches();
  const completedMatches = getCompletedMatches();

  // Always show all matches since we removed the filter tabs
  const filteredMatches = Array.isArray(matches) ? matches : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="text-center py-8 text-red-500">
        <p>Tournament not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Inject arrow styles */}
      <style dangerouslySetInnerHTML={{ __html: arrowStyles }} />
      
      {/* Match Results */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            Match Results
          </h3>
          
          {/* Team Points Display */}
          <div className="flex bg-gray-100 rounded-lg p-1 max-w-full overflow-x-auto">
            <div className="flex-1 px-3 py-2 rounded-md text-sm font-medium text-center">
              <div className="text-red-600 font-bold">
                {(() => {
                  const teamA = players.filter(p => p.teamAssignment === 'A');
                  const teamAWins = completedMatches.filter(m => m.winner === 'team1').length;
                  const ties = completedMatches.filter(m => m.winner === 'tie').length;
                  return teamAWins + (ties * 0.5);
                })()}
              </div>
              <div className="text-xs text-gray-600">Team A Points</div>
            </div>
            <div className="flex-1 px-3 py-2 rounded-md text-sm font-medium text-center">
              <div className="text-blue-600 font-bold">
                {(() => {
                  const teamB = players.filter(p => p.teamAssignment === 'B');
                  const teamBWins = completedMatches.filter(m => m.winner === 'team2').length;
                  const ties = completedMatches.filter(m => m.winner === 'tie').length;
                  return teamBWins + (ties * 0.5);
                })()}
              </div>
              <div className="text-xs text-gray-600">Team B Points</div>
            </div>
          </div>
        </div>

        {/* Compact Match List - Table Style */}
        <div className="space-y-2">
          {filteredMatches.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p>No matches found</p>
            </div>
          ) : (
            filteredMatches
              .sort((a, b) => a.matchNumber - b.matchNumber)
              .map((match) => {
                const teamNames = getTeamNames(match, players);
                const status = getMatchStatus(match);

                return (
                  <div
                    key={match.id}
                    className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {/* Star Icon */}
                    <div className="flex-shrink-0 mr-3">
                      <Star className="w-4 h-4 text-gray-300" />
                    </div>
                    
                    {/* Team A */}
                    <div className={`flex-1 text-sm font-medium ${
                      status.leadingTeam === 'A' ? 'text-red-600 font-bold' : 'text-gray-900'
                    }`}>
                      {teamNames.team1}
                    </div>
                    
                    {/* Status */}
                    <div className="flex-shrink-0 mx-3">
                      {status.arrow ? (
                        <div className={`px-3 py-1 text-xs font-medium ${status.color} ${
                          status.arrow === '←' ? 'clip-path-arrow-left' : 'clip-path-arrow-right'
                        }`}>
                          {status.text}
                        </div>
                      ) : (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          {status.text}
                        </span>
                      )}
                    </div>
                    
                    {/* Team B */}
                    <div className={`flex-1 text-sm font-medium text-right ${
                      status.leadingTeam === 'B' ? 'text-blue-600 font-bold' : 'text-gray-900'
                    }`}>
                      {teamNames.team2}
                    </div>
                  </div>
                );
              })
          )}
        </div>
        
        {/* Footer with Team Names */}
        <div className="mt-6 bg-gray-800 text-white rounded-lg p-3">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center">
              <span className="mr-2">ℹ</span>
              <span>Team A</span>
            </div>
            <div className="flex items-center">
              <span>Team B</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 